'use client';

import React, { FC } from 'react';

import { Document } from '@contentful/rich-text-types';

import classNames from 'classnames';

import AccessibilityLink from '@/components/common/AccessibilityLink/AccessibilityLink';
import RichText from '@/components/common/RichText/RichText';

import { TypeGlobalNavFields } from '@/core/generated-types';

import useLocale from '@/hooks/useLocale';

import { Link } from '@/navigation';

import { buildLinkUrl } from '@/utils/linkBuilder';
import {
  CLICKED_ITEM_TYPES,
  PAGE_LOCATIONS,
  trackItemClickedEvent,
} from '@/utils/rudderstack';
import { shouldShowMessageBanner } from './helpers';

export const MessageBanner: FC<TypeGlobalNavFields> = ({
  bannerText,
  bannerLinks,
  bannerBackgroundColor,
  bannerTextColor,
  enableAccessibilityLabel = 'Enable Accessibility',
  showAccessibilityLink,
  containerWidth = '1200px',
}) => {
  const locale = useLocale();

  const shouldShowBanner = shouldShowMessageBanner({
    bannerText,
    bannerLinks,
    showAccessibilityLink,
  });

  if (!shouldShowBanner) {
    return null;
  }

  return (
    <div
      className={classNames('h-[53px]', {
        [`bg-${bannerBackgroundColor?.fields?.color}`]: bannerBackgroundColor,
        'bg-blue-deep': !bannerBackgroundColor,
        [`text-${bannerTextColor?.fields?.color}`]: bannerTextColor,
        'text-white': !bannerTextColor,
      })}
      data-testid="message-banner"
    >
      <div
        className={`flex max-w-[${containerWidth}] mx-auto px-5 lg:px-4 justify-between items-center h-full`}
      >
        <div className="text-sm pr-4">
          <RichText
            openLinkSameWindow={true}
            content={bannerText as Document}
          />
        </div>

        <div className="flex items-center gap-4">
          {bannerLinks && (
            <div className="items-center gap-4 hidden md:flex">
              {bannerLinks.map((link) => {
                const href = buildLinkUrl(link, locale);

                return (
                  <Link
                    key={link.sys.id}
                    href={href}
                    className="text-sm hover:underline transition-all duration-200"
                    onClick={() => {
                      trackItemClickedEvent({
                        url: window.location.href,
                        type: CLICKED_ITEM_TYPES.LINK,
                        pageLocation: PAGE_LOCATIONS.NAVIGATION,
                        cta: link.fields.linkText || '',
                      });
                    }}
                  >
                    {(link.fields as any).linkText || 'Link'}
                  </Link>
                );
              })}
            </div>
          )}

          {showAccessibilityLink && (
            <AccessibilityLink
              enableAccessibilityLabel={enableAccessibilityLabel}
              className="text-sm underline md:no-underline md:hover:underline transition-all duration-200 text-right lg:text-left"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(MessageBanner);
